<template>
  <div class="professional-association-component">
    <div
      class="service-header mb-3 d-flex align-items-center justify-content-between"
    >
      <div>
        <h6 class="fs-6 fw-semibold text-dark mb-1 mt-0">
          Professional Association Check
        </h6>
        <p class="text-xs text-muted mb-0 lh-sm">
          Verify professional association memberships and certifications.
        </p>
      </div>
    </div>

    <!-- Multiple Professional Association Entries -->
    <div
      v-for="(association_entry, index) in professional_association_entries"
      :key="index"
      class="association-entry mb-4"
    >
      <div
        class="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom"
      >
        <h6 class="fs-7 text-muted mb-0">
          Professional Association Entry {{ index + 1 }}
        </h6>
        <button
          type="button"
          class="btn btn-bg-danger rounded-3 btn-sm text-xs"
          @click="remove_association_entry(index)"
          v-if="professional_association_entries.length > 1"
        >
          <i class="fas fa-times"></i>
          Remove
        </button>
      </div>

      <div class="row g-2 mb-2">
        <div class="col-md-12">
          <label class="form-label text-xs fw-medium text-secondary">
            In what region was this professional association obtained?
          </label>
          <div class="d-flex align-items-center">
            <div class="form-check me-3">
              <input
                class="form-check-input"
                type="radio"
                :name="'association_region_' + index"
                value="South Africa"
                :id="'association_region_sa_' + index"
                v-model="association_entry.association_region"
                @change="handle_region_change(index)"
              />
              <label
                class="form-check-label text-xs"
                :for="'association_region_sa_' + index"
                >South Africa</label
              >
            </div>
            <div class="form-check me-3">
              <input
                class="form-check-input"
                type="radio"
                :name="'association_region_' + index"
                value="Africa"
                :id="'association_region_africa_' + index"
                v-model="association_entry.association_region"
                @change="handle_region_change(index)"
              />
              <label
                class="form-check-label text-xs"
                :for="'association_region_africa_' + index"
                >Africa</label
              >
            </div>
            <div class="form-check">
              <input
                class="form-check-input"
                type="radio"
                :name="'association_region_' + index"
                value="Global"
                :id="'association_region_global_' + index"
                v-model="association_entry.association_region"
                @change="handle_region_change(index)"
              />
              <label
                class="form-check-label text-xs"
                :for="'association_region_global_' + index"
                >Global</label
              >
            </div>
          </div>
        </div>
      </div>

      <!-- South Africa Professional Association -->
      <div
        class="row g-2"
        v-if="association_entry.association_region === 'South Africa'"
      >
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Country <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter country"
            :disabled="association_entry.association_region === 'South Africa'"
            v-model="association_entry.country"
            :id="`association-country-${index}`"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Association Name <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter association name"
            v-model="association_entry.name_of_association"
            :id="`association-name-${index}`"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Membership Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter membership number"
            v-model="association_entry.membership_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Year (Optional)</label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter year"
            v-model="association_entry.year"
          />
        </div>
      </div>

      <!-- Africa Professional Association -->
      <div
        class="row g-2"
        v-else-if="association_entry.association_region === 'Africa'"
      >
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Country <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter country"
            v-model="association_entry.country"
            :id="`association-country-${index}`"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Association Name <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter association name"
            v-model="association_entry.name_of_association"
            :id="`association-name-${index}`"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Membership Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter membership number"
            v-model="association_entry.membership_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Year (Optional)</label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter year"
            v-model="association_entry.year"
          />
        </div>
      </div>

      <!-- Global Professional Association -->
      <div
        class="row g-2"
        v-else-if="association_entry.association_region === 'Global'"
      >
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Country <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter country"
            v-model="association_entry.country"
            :id="`association-country-${index}`"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Association Name <span class="text-danger">*</span>
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter association name"
            v-model="association_entry.name_of_association"
            :id="`association-name-${index}`"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary">
            Membership Number (Optional)
          </label>
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter membership number"
            v-model="association_entry.membership_number"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label text-xs fw-medium text-secondary"
            >Year (Optional)</label
          >
          <input
            type="text"
            class="form-control form-control-sm text-xs"
            placeholder="Enter year"
            v-model="association_entry.year"
          />
        </div>
      </div>
    </div>

    <!-- Add Professional Association Entry Button -->
    <div
      class="add-entry-section mt-3"
      v-if="professional_association_entries.length < 3"
    >
      <button
        type="button"
        class="btn btn-bg-success rounded-3 btn-sm text-xs"
        @click="add_association_entry"
      >
        <i class="fas fa-plus"></i>
        Add Another Professional Association
      </button>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { API_BASE_URL } from "@/config";

export default {
  name: "ProfessionalAssociation",
  props: {
    service_id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      professional_association_entries: [this.create_empty_association_entry()],
    };
  },
  methods: {
    create_empty_association_entry() {
      return {
        id: "",
        association_region: "",
        country: "",
        name_of_association: "",
        membership_number: "",
        year: "",
      };
    },

    add_association_entry() {
      if (this.professional_association_entries.length < 3) {
        this.professional_association_entries.push(
          this.create_empty_association_entry()
        );
      }
    },

    async remove_association_entry(index) {
      const entry = this.professional_association_entries[index];
      if (entry.id) {
        const confirmed = await Swal.fire({
          icon: "warning",
          title: "Are you sure?",
          text: "This will permanently remove the professional association entry and cannot be undone.",
          showCancelButton: true,
          confirmButtonText: "Yes, remove it",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
        });
        if (!confirmed.isConfirmed) return;

        const token = localStorage.getItem("token");
        try {
          const response = await fetch(`${API_BASE_URL}/verify_api/employee_screening/api/keys/remove/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Token ${token}`,
            },
            body: JSON.stringify({ employee_screening_service_key_group_id: entry.id }),
          });
          const data = await response.json();
          if (data.status === "success") {
            if (this.professional_association_entries.length > 1) {
              this.professional_association_entries.splice(index, 1);
            }
            Swal.fire({
              icon: "success",
              title: "Removed",
              text: "Professional association entry removed successfully.",
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: data.message || "Failed to remove professional association entry.",
            });
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to remove professional association entry.",
          });
        }
      } else {
        if (this.professional_association_entries.length > 1) {
          this.professional_association_entries.splice(index, 1);
        }
      }
    },

    set_field_invalid(field_id) {
      const el = document.getElementById(field_id);
      if (el) el.classList.add("border-danger");
    },
    set_field_valid(field_id) {
      const el = document.getElementById(field_id);
      if (el) el.classList.remove("border-danger");
    },

    validate_association_region(entry, index) {
      if (!entry.association_region) {
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: `Region is required for entry ${index + 1}.`,
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      return true;
    },
    validate_country(entry, index) {
      if (!entry.country) {
        this.set_field_invalid(`association-country-${index}`);
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: `Country is required for entry ${index + 1}.`,
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      this.set_field_valid(`association-country-${index}`);
      return true;
    },
    validate_association_name(entry, index) {
      if (!entry.name_of_association) {
        this.set_field_invalid(`association-name-${index}`);
        Swal.fire({
          icon: "info",
          title: "Validation Error",
          text: `Association name is required for entry ${index + 1}.`,
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
        return false;
      }
      this.set_field_valid(`association-name-${index}`);
      return true;
    },

    validate_information() {
      for (let i = 0; i < this.professional_association_entries.length; i++) {
        const entry = this.professional_association_entries[i];
        // Validate region
        if (!this.validate_association_region(entry, i)) return false;
        // Validate country
        if (!this.validate_country(entry, i)) return false;
        // Validate association name
        if (!this.validate_association_name(entry, i)) return false;
      }
      return true;
    },

    handle_data_submit() {
      if (this.validate_information()) {
        this.$emit(
          "update_data",
          "professional_association",
          this.professional_association_entries
        );
        return true;
      }
      return false;
    },

    handle_region_change(index) {
      const entry = this.professional_association_entries[index];
      if (entry.association_region === "South Africa") {
        entry.country = "South Africa";
      } else if (entry.country === "South Africa") {
        entry.country = "";
      }
    },
    fetch_data() {
      const token = localStorage.getItem("token");

      var workflow_id = localStorage.getItem("workflow_id") || null;
      if (!workflow_id) {
        console.error("Workflow ID is not set in localStorage.");
        return;
      }

      // Build query string
      const params = new URLSearchParams({
        workflow_id: workflow_id,
        service_id: this.service_id,
      });

      fetch(
        `${API_BASE_URL}/verify_api/employee_screening/api/get_sections_by_workflow_client/?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      )
        .then((response) => response.json())
        .then((data) => {
          if (data.status === "success" && data.data) {
            var assigned_services = data.data.sections || [];
            const service =
              assigned_services.length > 0 ? assigned_services[0] : null;
            if (
              service &&
              service.key_groups &&
              Array.isArray(service.key_groups) &&
              service.key_groups.length > 0
            ) {
              // Map each key_group to a professional_association_entry
              this.professional_association_entries = service.key_groups.map(
                (key_group) => {
                  // Flatten key_mappings into an object
                  let entry = {};
                  if (key_group.key_mappings && Array.isArray(key_group.key_mappings)) {
                    key_group.key_mappings.forEach(mapping => {
                      entry[mapping.key_name] = mapping.key_value;
                    });
                  }

                  let category = key_group.category || {};
                  let association_region = "";

                  if (category.category_name) {
                    if (category.category_name === "South African Only") {
                      association_region = "South Africa";
                    } else if (category.category_name === "Africa") {
                      association_region = "Africa";
                    } else if (category.category_name === "Global") {
                      association_region = "Global";
                    }
                  }

                  return {
                    id: key_group.id || "",
                    association_region: association_region,
                    country: entry.country || "",
                    name_of_association: entry.name_of_association || "",
                    membership_number: entry.membership_number || "",
                    year: entry.year || "",
                  };
                }
              );
            } else {
              // If no key_groups, reset to one empty entry
              this.professional_association_entries = [
                this.create_empty_association_entry(),
              ];
            }
          } else {
            this.professional_association_entries = [
              this.create_empty_association_entry(),
            ];
          }
        })
        .catch((error) => {
          console.error("Error fetching client sections:", error);
        });
    },
  },
  mounted() {
    this.fetch_data();
  },
};
</script>

<style scoped>
.fs-7 {
  font-size: 0.875rem;
}

.service-header {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f3f4;
}

.form-label {
  margin-bottom: 0.25rem;
}

.form-control-sm {
  padding: 0.375rem 0.5rem;
}

.association-entry {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
}

.add-entry-section {
  text-align: center;
}
</style>
